// Platform Administration's calls on the identity server (the identity ADR, point 10; the canon's
// "Managing Humans, Machines and their sessions"): what the Humans and Machines screens read
// and write. The routes live on the identity server under `/administration`, so they ride the
// same transport as `/session/*` — `identityClient.sendRequest` — and with it the same
// credential (the session's token and a proof for exactly this URL) and the same 401 handling.
//
// Lists answer as the administration server's do: `{ meta: { totalCount }, documents }`. An
// absent parameter is not sent, so the server applies its own defaults.
//
// A secret is answered once. `machines.create` and `machines.rotateCredential` answer the new
// client secret in that one response and nothing here keeps it: the caller shows it and lets it
// go.
import { identityClient } from './identityClient.js';

const PREFIX = '/administration';

function administrationUrl(path, query = {}) {
    const params = new URLSearchParams();
    for (const [name, value] of Object.entries(query)) {
        if (value !== undefined && value !== null && value !== '') {
            params.set(name, String(value));
        }
    }
    const search = params.toString();
    if (search === '') {
        return `${identityClient.baseUri}${PREFIX}${path}`;
    }
    return `${identityClient.baseUri}${PREFIX}${path}?${search}`;
}

function humanPath(humanId, rest = '') {
    return `/humans/${encodeURIComponent(humanId)}${rest}`;
}

function machinePath(machineId, rest = '') {
    return `/machines/${encodeURIComponent(machineId)}${rest}`;
}

export const humansClient = {
    /** A page of people, by part of a name or an email: `{ meta: { totalCount }, documents }`. */
    list({ q, page, pageSize } = {}) {
        return identityClient.sendRequest(administrationUrl('/humans', { q, page, pageSize }), 'GET');
    },

    /** One person: who they are, their logins, their devices and their sessions. */
    read(humanId) {
        return identityClient.sendRequest(administrationUrl(humanPath(humanId)), 'GET');
    },

    /**
     * End one of the person's devices, named by its key thumbprint (the session row's `id`), or
     * every session they have when no device is named.
     */
    endSessions(humanId, { device } = {}) {
        const body = {};
        if (device !== undefined) {
            body.device = device;
        }
        return identityClient.sendRequest(administrationUrl(humanPath(humanId, '/sessions/end')), 'POST', body);
    },

    /**
     * Close the person's account: contact details stripped, logins dormant, every session
     * ended. The person restores it by signing in again with any old login. Answers
     * `{ humanId, closed: true }`.
     */
    close(humanId) {
        return identityClient.sendRequest(administrationUrl(humanPath(humanId, '/close')), 'POST');
    }
};

export const machinesClient = {
    /** A page of programs, all or by status: `{ meta: { totalCount }, documents }`. */
    list({ status, page, pageSize } = {}) {
        return identityClient.sendRequest(administrationUrl('/machines', { status, page, pageSize }), 'GET');
    },

    /** One program: what it is, its credentials, and every live token with where it was used. */
    read(machineId) {
        return identityClient.sendRequest(administrationUrl(machinePath(machineId)), 'GET');
    },

    /** Make a program. Answers `{ machineId, clientId, clientSecret, secretShownOnce }`, the secret once. */
    create({ name }) {
        return identityClient.sendRequest(administrationUrl('/machines'), 'POST', { name });
    },

    /** Suspend a program (`suspended`) or let it back in (`active`); its credentials are untouched. */
    setStatus(machineId, status) {
        return identityClient.sendRequest(administrationUrl(machinePath(machineId, '/status')), 'POST', { status });
    },

    /**
     * A second credential beside the first; the first keeps working for `expiresInHours` (the
     * server's 24 when absent), then is revoked. Answers `{ machineId, clientId, clientSecret,
     * expiring: [{ clientId, expiresAt }], expiresInHours, secretShownOnce }`, the secret once.
     */
    rotateCredential(machineId, { expiresInHours } = {}) {
        const body = {};
        if (expiresInHours !== undefined) {
            body.expiresInHours = expiresInHours;
        }
        return identityClient.sendRequest(administrationUrl(machinePath(machineId, '/credentials')), 'POST', body);
    },

    /** Revoke one credential: its pool client is deleted and every token it made is stale. */
    revokeCredential(machineId, clientId) {
        return identityClient.sendRequest(administrationUrl(machinePath(machineId, `/credentials/${encodeURIComponent(clientId)}`)), 'DELETE');
    },

    /** Make tokens stale: one by `tokenId`, every one a secret made by `secretFingerprint`, or all of them when neither is named. */
    revokeTokens(machineId, { tokenId, secretFingerprint } = {}) {
        const body = {};
        if (tokenId !== undefined) {
            body.tokenId = tokenId;
        }
        if (secretFingerprint !== undefined) {
            body.secretFingerprint = secretFingerprint;
        }
        return identityClient.sendRequest(administrationUrl(machinePath(machineId, '/tokens/revoke')), 'POST', body);
    },

    /** Retire a program: every credential destroyed, every token stale, the record kept. */
    retire(machineId) {
        return identityClient.sendRequest(administrationUrl(machinePath(machineId, '/retire')), 'POST');
    }
};

export const administrationClient = { humans: humansClient, machines: machinesClient };
export default administrationClient;
